import { useState, useEffect, useCallback } from "react";
import { Table, Typography, Tag, Empty, Spin, Card, Timeline } from "antd";
import {
  DeleteOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FilePptOutlined,
  FileImageOutlined,
  FileTextOutlined,
  FileZipOutlined,
  DownloadOutlined,
  EyeOutlined,
  ShareAltOutlined,
  UploadOutlined,
  LoadingOutlined,
  FolderOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { VaultFile, ActivityLog } from "@/types";
import { listDeletedFiles } from "@/services/storageManager";
import { getResourceActivity } from "@/services/activity";
import { formatFileSize, getRelativeTimeString, formatFullDate } from "@/utils/formatters";
import { getFileConfig } from "@/utils/fileIcons";
import { ACTIVITY_ACTIONS } from "@/utils/constants";

const { Title, Text } = Typography;

// Map icon names to components
const iconMap: Record<string, React.ReactNode> = {
  FilePdfOutlined: <FilePdfOutlined />,
  FileExcelOutlined: <FileExcelOutlined />,
  FileWordOutlined: <FileWordOutlined />,
  FilePptOutlined: <FilePptOutlined />,
  FileImageOutlined: <FileImageOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  FileMarkdownOutlined: <FileTextOutlined />,
  FileZipOutlined: <FileZipOutlined />,
  VideoCameraOutlined: <FileOutlined />,
  AudioOutlined: <FileOutlined />,
  FileOutlined: <FileOutlined />,
};

// Map activity actions to icons
const actionIconMap: Record<string, React.ReactNode> = {
  upload: <UploadOutlined style={{ color: "#10B981" }} />,
  download: <DownloadOutlined style={{ color: "#3B82F6" }} />,
  view: <EyeOutlined style={{ color: "#6B7280" }} />,
  share: <ShareAltOutlined style={{ color: "#8B5CF6" }} />,
  delete: <DeleteOutlined style={{ color: "#EF4444" }} />,
  unshare: <ShareAltOutlined style={{ color: "#F97316" }} />,
};

function getFileIconComponent(mimeType: string): React.ReactNode {
  const config = getFileConfig(mimeType);
  return iconMap[config.icon] || <FileOutlined />;
}

// Activity row component for expanded view
function ActivityTimeline({ fileId }: { fileId: string }) {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadActivity();
  }, [fileId]);

  const loadActivity = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getResourceActivity(fileId, 50);
      setActivities(data);
    } catch (err) {
      console.error("Failed to load activity:", err);
      setError("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin indicator={<LoadingOutlined spin />} />
        <Text type="secondary" style={{ marginLeft: 8 }}>Loading activity...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Text type="secondary">No activity logs found</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 24px", background: "#FAFAFA" }}>
      <Text strong style={{ display: "block", marginBottom: 16 }}>
        Activity History
      </Text>
      <Timeline
        items={activities.map((activity) => ({
          dot: actionIconMap[activity.action] || <ClockCircleOutlined />,
          children: (
            <div>
              <Text>
                <Text strong>{activity.performedBy.name}</Text>
                {" "}
                {ACTIVITY_ACTIONS[activity.action as keyof typeof ACTIVITY_ACTIONS] || activity.action}
                {" "}
                this file
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatFullDate(activity.performedAt)}
                {activity.ipAddress && ` • ${activity.ipAddress}`}
              </Text>
            </div>
          ),
        }))}
      />
    </div>
  );
}

export default function ShredderPage() {
  const [files, setFiles] = useState<VaultFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  useEffect(() => {
    loadDeletedFiles();
  }, []);

  const loadDeletedFiles = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listDeletedFiles();
      setFiles(response.data);
    } catch (err) {
      console.error("Failed to load deleted files:", err);
      setError("Failed to load deleted files");
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = useCallback((expanded: boolean, record: VaultFile) => {
    if (expanded) {
      setExpandedRowKeys([record.id]);
    } else {
      setExpandedRowKeys([]);
    }
  }, []);

  const columns: ColumnsType<VaultFile> = [
    {
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      width: 280,
      ellipsis: true,
      render: (fileName: string, record: VaultFile) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 20, opacity: 0.5 }}>
            {getFileIconComponent(record.mimeType)}
          </span>
          <div style={{ minWidth: 0 }}>
            <Text style={{ display: "block", color: "#374151" }} ellipsis={{ tooltip: fileName }}>
              {fileName}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatFileSize(record.size)}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Deleted",
      dataIndex: "deletedAt",
      key: "deletedAt",
      width: 150,
      render: (deletedAt: string) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ClockCircleOutlined style={{ color: "#9CA3AF" }} />
          <Text type="secondary">{getRelativeTimeString(deletedAt)}</Text>
        </div>
      ),
      sorter: (a, b) => new Date(b.deletedAt!).getTime() - new Date(a.deletedAt!).getTime(),
      defaultSortOrder: "ascend",
    },
    {
      title: "Type",
      dataIndex: "deletionType",
      key: "deletionType",
      width: 130,
      render: (deletionType: "manual" | "auto" | null) => {
        if (deletionType === "auto") {
          return (
            <Tag icon={<ClockCircleOutlined />} color="orange">
              Auto-expired
            </Tag>
          );
        }
        return (
          <Tag icon={<UserOutlined />} color="blue">
            Manual
          </Tag>
        );
      },
      filters: [
        { text: "Manual", value: "manual" },
        { text: "Auto-expired", value: "auto" },
      ],
      onFilter: (value, record) => record.deletionType === value,
    },
    {
      title: "Location",
      dataIndex: "deletedFromFolder",
      key: "deletedFromFolder",
      width: 180,
      ellipsis: true,
      render: (folderName: string | null) => {
        if (folderName) {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FolderOutlined style={{ color: "#10B981", flexShrink: 0 }} />
              <Text type="secondary" ellipsis={{ tooltip: folderName }}>{folderName}</Text>
            </div>
          );
        }
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <HomeOutlined style={{ color: "#9CA3AF" }} />
            <Text type="secondary">Root</Text>
          </div>
        );
      },
    },
    {
      title: "Uploaded",
      dataIndex: "uploadedAt",
      key: "uploadedAt",
      width: 150,
      render: (uploadedAt: string) => (
        <Text type="secondary">{getRelativeTimeString(uploadedAt)}</Text>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <DeleteOutlined style={{ fontSize: 28, color: "#EF4444" }} />
          <Title level={2} style={{ margin: 0 }}>
            Shredder
          </Title>
        </div>
        <Text type="secondary">
          Files that have been deleted, either manually or automatically after 30 days. Click a row to view activity history.
        </Text>
      </div>

      {/* Content */}
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
        }}
        styles={{ body: { padding: 0 } }}
      >
        {error ? (
          <div style={{ padding: 48, textAlign: "center" }}>
            <Text type="danger">{error}</Text>
          </div>
        ) : files.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No deleted files"
            style={{ padding: 48 }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={files}
            rowKey="id"
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
              expandedRowRender: (record) => <ActivityTimeline fileId={record.id} />,
              expandRowByClick: true,
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${total} deleted files`,
            }}
            style={{ borderRadius: 12, overflow: "hidden" }}
            rowClassName={() => "cursor-pointer"}
          />
        )}
      </Card>
    </div>
  );
}
